import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Coins, ChevronLeft, ChevronRight, Users2, User, Settings2, Gift, TrendingUp, Info } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api, apiErrorMessage } from "@/lib/api";

const eur = (n) => `${Number(n || 0).toLocaleString("es-ES")}€`;
const fmtDate = (s) => (s ? new Date(s + "T00:00:00").toLocaleDateString("es-ES") : "—");

function PeriodNav({ label, offset, setOffset }) {
  return (
    <div className="flex items-center gap-2" data-testid="com-period-nav">
      <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setOffset(offset - 1)} data-testid="com-prev"><ChevronLeft className="h-4 w-4" /></Button>
      <span className="min-w-[190px] text-center text-sm font-semibold" data-testid="com-period-label">{label || "…"}</span>
      <Button variant="outline" size="icon" className="h-8 w-8" disabled={offset >= 0} onClick={() => setOffset(offset + 1)} data-testid="com-next"><ChevronRight className="h-4 w-4" /></Button>
    </div>
  );
}

function SheetView({ data }) {
  if (!data) return null;
  const t = data.totals;
  return (
    <Card data-testid="com-sheet">
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">#</TableHead>
                <TableHead>Fecha venta</TableHead>
                <TableHead>Cliente (lead)</TableHead>
                <TableHead className="text-center">Pax</TableHead>
                <TableHead className="text-center">Días</TableHead>
                <TableHead className="text-right">€/pax</TableHead>
                <TableHead className="text-right">Importe</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.sales.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="py-8 text-center text-sm text-muted-foreground" data-testid="com-empty">Sin ventas en este periodo.</TableCell></TableRow>
              ) : data.sales.map((s) => (
                <TableRow key={s.trip_id} data-testid={`com-sale-${s.index}`}>
                  <TableCell className="text-muted-foreground">{s.index}</TableCell>
                  <TableCell>{fmtDate(s.sale_date)}</TableCell>
                  <TableCell className="font-medium">{s.lead}</TableCell>
                  <TableCell className="text-center">{s.pax}</TableCell>
                  <TableCell className="text-center">{s.days ?? "—"}</TableCell>
                  <TableCell className="text-right">
                    {eur(s.rate)}
                    {s.is_special && <Badge variant="outline" className="ml-1.5 border-amber-200 bg-amber-50 text-[10px] text-amber-700" title={s.reason}>{s.reason}</Badge>}
                  </TableCell>
                  <TableCell className="text-right font-semibold">{eur(s.subtotal)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <div className="space-y-1.5 border-t p-4 text-sm">
          <div className="flex justify-between"><span className="text-muted-foreground">Bruto ({t.total_pax} pax · {t.num_sales} ventas)</span><span className="font-medium">{eur(t.gross)}</span></div>
          <div className="flex justify-between text-rose-600"><span>− Exención primeros {t.exempt_pax_used} pax</span><span className="font-medium">−{eur(t.exemption)}</span></div>
          <div className="flex justify-between border-t pt-1.5"><span className="text-muted-foreground">Comisión por pasajeros</span><span className="font-semibold" data-testid="com-commission-pax">{eur(t.commission_pax)}</span></div>
          {t.bonus_detail.map((b, i) => (
            <div key={i} className="flex justify-between text-emerald-600"><span className="flex items-center gap-1"><Gift className="h-3.5 w-3.5" />Bonus {b.label}</span><span className="font-medium">+{eur(b.amount)}</span></div>
          ))}
          <div className="mt-1 flex items-center justify-between rounded-lg bg-primary/5 px-3 py-2">
            <span className="font-heading text-base font-bold">TOTAL DEL PERIODO</span>
            <span className="font-heading text-lg font-bold text-primary" data-testid="com-total">{eur(t.total)}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function TeamView({ offset, onOpenAgent }) {
  const q = useQuery({ queryKey: ["com-resumen", offset], queryFn: async () => (await api.get("/comisiones/resumen", { params: { offset } })).data });
  if (q.isLoading) return <Skeleton className="h-64 w-full" />;
  const d = q.data;
  return (
    <Card data-testid="com-team">
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Agente</TableHead>
                <TableHead className="text-center">Ventas</TableHead>
                <TableHead className="text-center">Pax</TableHead>
                <TableHead className="text-right">Comisión pax</TableHead>
                <TableHead className="text-right">Bonus</TableHead>
                <TableHead className="text-right">Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {d.rows.map((r) => (
                <TableRow key={r.agent} className="cursor-pointer hover:bg-muted/40" onClick={() => onOpenAgent(r.agent)} data-testid={`com-row-${r.agent}`}>
                  <TableCell className="font-medium">{r.agent}</TableCell>
                  <TableCell className="text-center">{r.num_sales}</TableCell>
                  <TableCell className="text-center">{r.total_pax}</TableCell>
                  <TableCell className="text-right">{eur(r.commission_pax)}</TableCell>
                  <TableCell className="text-right text-emerald-600">{r.bonus ? `+${eur(r.bonus)}` : "—"}</TableCell>
                  <TableCell className="text-right font-semibold">{eur(r.total)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <div className="flex items-center justify-between border-t bg-primary/5 px-4 py-3">
          <span className="font-heading font-bold">Total equipo</span>
          <span className="font-heading text-lg font-bold text-primary" data-testid="com-grand-total">{eur(d.grand_total)}</span>
        </div>
      </CardContent>
    </Card>
  );
}

function ConfigDialog({ open, onClose }) {
  const qc = useQueryClient();
  const cfgQ = useQuery({ queryKey: ["com-config"], queryFn: async () => (await api.get("/comisiones/config")).data, enabled: open });
  const [form, setForm] = useState(null);
  React.useEffect(() => { if (cfgQ.data && open) setForm(cfgQ.data); }, [cfgQ.data, open]);
  const saveMut = useMutation({
    mutationFn: async () => (await api.put("/comisiones/config", form)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["com-config"] }); qc.invalidateQueries({ queryKey: ["com-mi-hoja"] }); qc.invalidateQueries({ queryKey: ["com-resumen"] }); toast.success("Modelo de comisiones actualizado"); onClose(); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });
  const FIELDS = [
    ["base_rate", "€ por pax (base)"], ["special_rate", "€ por pax (especial)"],
    ["exempt_pax", "Pax exentos"], ["exempt_rate", "€ exención por pax"],
    ["long_trip_days", "Días para 'viaje largo'"], ["special_from_sale", "Especial desde venta nº"],
    ["bonus1_sales", "Bonus 1 · ventas"], ["bonus1_amount", "Bonus 1 · importe €"],
    ["bonus2_sales", "Bonus 2 · ventas"], ["bonus2_amount", "Bonus 2 · importe €"],
    ["heidi_from_sale", "Externalizar desde venta nº"],
    ["team_avg5", "Equipo · media ventas (nivel 1)"], ["team_per_agent_5", "Equipo · €/agente (nivel 1)"],
    ["team_avg7", "Equipo · media ventas (nivel 2)"], ["team_per_agent_7", "Equipo · €/agente extra (nivel 2)"],
    ["team_min_agents", "Equipo · nº agentes para extra"], ["team_extra_min6", "Equipo · extra si nº agentes €"],
    ["team_extra_all", "Equipo · extra si todos €"],
  ];
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="com-config-dialog">
        <DialogHeader><DialogTitle>Modelo de comisiones</DialogTitle></DialogHeader>
        {!form ? <Skeleton className="h-64 w-full" /> : (
          <div className="grid grid-cols-2 gap-3">
            {FIELDS.map(([k, label]) => (
              <div key={k} className="space-y-1.5">
                <Label className="text-xs">{label}</Label>
                <Input type="number" value={form[k] ?? ""} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value === "" ? null : Number(e.target.value) }))} data-testid={`com-cfg-${k}`} />
              </div>
            ))}
            <div className="col-span-2 space-y-1.5">
              <Label className="text-xs">Nombre del agente de reservas (Heidi)</Label>
              <Input value={form.heidi_user_name || ""} onChange={(e) => setForm((f) => ({ ...f, heidi_user_name: e.target.value }))} data-testid="com-cfg-heidi" />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={() => saveMut.mutate()} disabled={saveMut.isPending || !form} data-testid="com-cfg-save">{saveMut.isPending ? "Guardando…" : "Guardar"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TeamQuarterView() {
  const [qoffset, setQoffset] = useState(0);
  const q = useQuery({ queryKey: ["com-equipo-tri", qoffset], queryFn: async () => (await api.get("/comisiones/equipo-trimestre", { params: { offset: qoffset } })).data });
  return (
    <div className="space-y-3" data-testid="com-quarter">
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">Comisión de la Jefa de Equipo (Director of Sales) · trimestre natural</span>
        <PeriodNav label={q.data?.quarter?.label} offset={qoffset} setOffset={setQoffset} />
      </div>
      {q.isLoading ? <Skeleton className="h-64 w-full" /> : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Agente</TableHead>
                    <TableHead className="text-center">Ventas trimestre</TableHead>
                    <TableHead className="text-center">Media/mes</TableHead>
                    <TableHead className="text-right">Aporta a Jefa de Equipo</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {q.data.rows.map((r) => (
                    <TableRow key={r.agent} data-testid={`com-q-${r.agent}`}>
                      <TableCell className="font-medium">{r.agent}</TableCell>
                      <TableCell className="text-center">{r.num_sales}</TableCell>
                      <TableCell className="text-center">
                        <span className={r.hit7 ? "font-semibold text-emerald-600" : r.hit5 ? "font-medium text-amber-600" : "text-muted-foreground"}>{r.avg}</span>
                        {r.hit7 && <Badge variant="outline" className="ml-1.5 border-emerald-200 bg-emerald-50 text-[10px] text-emerald-700">≥7</Badge>}
                        {r.hit5 && !r.hit7 && <Badge variant="outline" className="ml-1.5 border-amber-200 bg-amber-50 text-[10px] text-amber-700">≥5</Badge>}
                      </TableCell>
                      <TableCell className="text-right font-semibold">{r.bonus ? eur(r.bonus) : "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="space-y-1.5 border-t p-4 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Por agentes ({q.data.agents_hit5}/{q.data.total_agents} con media ≥5)</span><span className="font-medium">{eur(q.data.per_agent_total)}</span></div>
              {q.data.extras.map((x, i) => (
                <div key={i} className="flex justify-between text-emerald-600"><span className="flex items-center gap-1"><TrendingUp className="h-3.5 w-3.5" />Extra · {x.label}</span><span className="font-medium">+{eur(x.amount)}</span></div>
              ))}
              <div className="mt-1 flex items-center justify-between rounded-lg bg-primary/5 px-3 py-2">
                <span className="font-heading text-base font-bold">TOTAL COMISIÓN DE EQUIPO</span>
                <span className="font-heading text-lg font-bold text-primary" data-testid="com-quarter-total">{eur(q.data.total)}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      <div className="flex items-start gap-2 rounded-lg border border-dashed p-3 text-xs text-muted-foreground">
        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span>100€ por agente con media ≥5 ventas/mes · +200€ más si media ≥7 (300€ en total) · +200€ si ≥6 agentes llegan a ≥5 · +400€ si llegan todos. Se suma a la comisión mensual de ventas de la Director of Sales.</span>
      </div>
    </div>
  );
}

export default function ComisionesTab() {
  const [offset, setOffset] = useState(0);
  const [view, setView] = useState("mine"); // mine | team | agent
  const [agent, setAgent] = useState(null);
  const [cfgOpen, setCfgOpen] = useState(false);

  const miQ = useQuery({ queryKey: ["com-mi-hoja", offset], queryFn: async () => (await api.get("/comisiones/mi-hoja", { params: { offset } })).data });
  const canViewAll = !!miQ.data?.can_view_all;
  const canViewTeamMonthly = !!miQ.data?.can_view_team_monthly;

  const agentQ = useQuery({
    queryKey: ["com-hoja", agent, offset],
    queryFn: async () => (await api.get("/comisiones/hoja", { params: { agent, offset } })).data,
    enabled: view === "agent" && !!agent,
  });

  const label = (view === "team" ? null : (view === "agent" ? agentQ.data : miQ.data)?.period?.label) || miQ.data?.period?.label;

  return (
    <div className="space-y-4" data-testid="com-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-heading text-2xl font-bold tracking-tight"><Coins className="h-6 w-6 text-primary" />Comisiones</h1>
          <p className="text-sm text-muted-foreground">Tu hoja de comisiones del periodo (del 16 al 15). Se completa sola con tus ventas.</p>
        </div>
        <div className="flex items-center gap-2">
          {view !== "quarter" && <PeriodNav label={label} offset={offset} setOffset={setOffset} />}
          {canViewAll && <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setCfgOpen(true)} data-testid="com-config-btn" title="Modelo de comisiones"><Settings2 className="h-4 w-4" /></Button>}
        </div>
      </div>

      {canViewAll && (
        <div className="flex flex-wrap gap-2">
          <Button variant={view === "mine" ? "default" : "outline"} size="sm" onClick={() => setView("mine")} data-testid="com-view-mine"><User className="mr-1.5 h-4 w-4" />Mi hoja</Button>
          {canViewTeamMonthly && <Button variant={view === "team" || view === "agent" ? "default" : "outline"} size="sm" onClick={() => { setView("team"); setAgent(null); }} data-testid="com-view-team"><Users2 className="mr-1.5 h-4 w-4" />Equipo (mensual)</Button>}
          <Button variant={view === "quarter" ? "default" : "outline"} size="sm" onClick={() => setView("quarter")} data-testid="com-view-quarter"><TrendingUp className="mr-1.5 h-4 w-4" />Comisión de equipo (trimestral)</Button>
          {view === "agent" && agent && <Badge variant="secondary" className="self-center">{agent}</Badge>}
        </div>
      )}

      {view === "mine" && (miQ.isLoading ? <Skeleton className="h-64 w-full" /> : <SheetView data={miQ.data} />)}
      {view === "team" && canViewTeamMonthly && <TeamView offset={offset} onOpenAgent={(a) => { setAgent(a); setView("agent"); }} />}
      {view === "agent" && (agentQ.isLoading ? <Skeleton className="h-64 w-full" /> : <SheetView data={agentQ.data} />)}
      {view === "quarter" && canViewAll && <TeamQuarterView />}

      <div className="flex items-start gap-2 rounded-lg border border-dashed p-3 text-xs text-muted-foreground">
        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span>Base 35€/pax; 60€/pax en viajes de +16 días o desde la 6ª venta. Se restan los primeros 4 pax del periodo. Bonus: +150€ a las 5 ventas y +150€ a las 7. Desde la 6ª venta las reservas se externalizan a Heidi.</span>
      </div>

      <ConfigDialog open={cfgOpen} onClose={() => setCfgOpen(false)} />
    </div>
  );
}
